// Las publicaciones de Mercado Libre en las primeras piezas
// (ADR-2026-10-19-mercadolibre-en-las-primeras-piezas).
//
// Al conectar Mercado Libre el backend arranca solo la importación masiva. Acá se cruza lo que HAY
// (el listado de publicaciones del backend, que ya dice a qué producto de Atlas quedó vinculada
// cada una) con la primera variante de cada producto, para mostrar los que se pueden elegir. Sale
// con la misma forma que el catálogo de Tiendanube: la grilla y los textos son los mismos.

import { supabase } from '../supabase';
import { claveDePublicacion, type PublicacionDeMl } from '../mercadoLibreCatalogoLogica';
import { enCurso, type EstadoDeImportaciones } from '../importacionesLogica';
import { estadoDeLaCarga, type CatalogoDeLaTienda, type EstadoDeLaCarga } from './catalogoDeLaTienda';

/** El listado le pega a la API de Mercado Libre (decenas de llamadas con cientos de publicaciones):
 *  mientras se importa se vuelve a pedir como mucho cada tanto. */
export const MS_ENTRE_LISTADOS = 15_000;

/** Recién conectada, la importación puede no figurar todavía: antes de esto no se da por terminada. */
export const MS_DE_GRACIA = 30_000;

/**
 * Junta las publicaciones con lo que ya llegó (producto de Atlas → su primera variante). Varias
 * publicaciones pueden ser UN producto (una por color en el modelo nuevo): se muestra una vez y
 * se cuenta una vez.
 */
export function cruzarPublicaciones(
  publicaciones: readonly PublicacionDeMl[],
  variantes: ReadonlyMap<string, string>,
): CatalogoDeLaTienda {
  const vistos = new Set<string>();
  const llegados: CatalogoDeLaTienda['llegados'] = [];
  let faltan = 0;
  for (const p of publicaciones) {
    if (!p.producto_id) {
      faltan++;
      continue;
    }
    if (vistos.has(p.producto_id)) continue;
    vistos.add(p.producto_id);
    const varianteId = variantes.get(p.producto_id);
    if (!varianteId) {
      faltan++;
      continue;
    }
    llegados.push({ clave: `ml:${claveDePublicacion(p)}`, nombre: p.titulo, imagen: p.miniatura ?? '', varianteId });
  }
  return { llegados, total: llegados.length + faltan, faltan };
}

/**
 * Cómo va la carga. Lo que no está vinculado no siempre va a llegar (una publicación de catálogo,
 * una que falló): el fin lo dice la importación, no la cuenta. Terminada, lo que llegó es el total.
 * Si no se pudo preguntar por la importación, se decide como en Tiendanube: por la cuenta y el tiempo.
 */
export function cargaDeMercadoLibre(p: {
  catalogo: CatalogoDeLaTienda;
  importaciones: EstadoDeImportaciones | null;
  msDesdeQueAbrio: number;
  msSinNovedad: number;
}): { catalogo: CatalogoDeLaTienda; carga: EstadoDeLaCarga } {
  const { catalogo, importaciones } = p;
  const porLaCuenta = estadoDeLaCarga({ faltan: catalogo.faltan, msSinNovedad: p.msSinNovedad });
  if (porLaCuenta === 'completo' || !importaciones || enCurso(importaciones.activa)) return { catalogo, carga: porLaCuenta };
  if (p.msDesdeQueAbrio < MS_DE_GRACIA && catalogo.llegados.length === 0) return { catalogo, carga: 'cargando' };
  return { catalogo: { ...catalogo, total: catalogo.llegados.length, faltan: 0 }, carga: 'completo' };
}

const TANDA = 200;

/** La primera variante activa de cada producto (la que se usa para generar). */
export async function primerasVariantes(companyId: string, productoIds: readonly string[]): Promise<Map<string, string>> {
  const primeras = new Map<string, string>();
  for (let i = 0; i < productoIds.length; i += TANDA) {
    const { data, error } = await supabase
      .from('variantes')
      .select('id, product_id, created_at')
      .eq('id_company', companyId)
      .eq('activo', true)
      .in('product_id', productoIds.slice(i, i + TANDA))
      .order('created_at', { ascending: true });
    if (error) throw error;
    for (const fila of (data ?? []) as Array<{ id: string; product_id: string | null }>) {
      if (fila.product_id && !primeras.has(fila.product_id)) primeras.set(fila.product_id, fila.id);
    }
  }
  return primeras;
}
